import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";
import { Footer } from "@/components/Footer";
import { Shield, Key, Wallet, Eye } from "lucide-react";

export const Route = createFileRoute("/security")({
  component: SecurityPage,
  head: () => ({
    meta: [
      { title: "Security - RobotDex" },
      { name: "description", content: "How RobotDex approaches security, wallet safety, and on-chain trust." },
    ],
  }),
});

function SecurityPage() {
  return (
    <div>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <PageHeader title="Security" subtitle="Our approach to keeping your wallet and NFTs safe" />
        <p className="text-sm text-muted-foreground max-w-2xl">
          This page is maintained by the RobotDex team to answer common security questions about the RobotDex marketplace. It describes app-visible controls only; it is not a certification.
        </p>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Item icon={Wallet} title="Non-custodial by design">
            RobotDex never holds your funds, private keys, or NFTs. All transactions are signed and broadcast directly by your wallet on the public blockchain.
          </Item>
          <Item icon={Key} title="You control your keys">
            The RobotDex team cannot recover your wallet, reverse transactions, or freeze assets. Store your seed phrase offline and never share it.
          </Item>
          <Item icon={Shield} title="Wallet integration via WalletConnect / Reown">
            Wallet connections go through the audited WalletConnect protocol, supporting 300+ wallets including MetaMask, Rainbow, Coinbase Wallet, Trust Wallet, and Ledger.
          </Item>
          <Item icon={Eye} title="Transparent on-chain fees">
            Platform fees and creator royalties are visible in the app and applied on-chain. The fee recipient address is set on-chain by the Platform.
          </Item>
          <Item icon={Shield} title="Public blockchain risk">
            Blockchains are public and permissionless. Third-party smart contracts, network congestion, and phishing attempts are outside our control. Always review transactions before signing.
          </Item>
        </div>

        <Card className="mt-6 p-6 bg-card/60 neon-border text-sm">
          <h2 className="font-semibold neon-text mb-2">Reporting a vulnerability</h2>
          <p className="text-muted-foreground">
            If you believe you have found a security issue in RobotDex, please contact us through the community channels listed in the footer with a clear description and, if possible, a proof of concept. Please do not publicly disclose the issue before we have had a chance to respond.
          </p>
        </Card>
      </div>
      <Footer />
    </div>
  );
}

function Item({ icon: Icon, title, children }: any) {
  return (
    <Card className="p-5 bg-card/60 neon-border">
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{children}</p>
        </div>
      </div>
    </Card>
  );
}
